import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PaymentInstructionService, { PaymentInstruction } from '../services/PaymentInstructionService';
import { formatIBAN, cleanIBAN, getIBANError } from '../../../utils/ibanUtil';
import { personalakteStyles as ps, colors, shadows } from '../styles/personalakteStyles';

interface PaymentInstructionModalProps {
  employeeId: number;
  employeeName: string;
  existingInstruction: PaymentInstruction | null;
  onClose: () => void;
  onSave: () => void;
}

const PaymentInstructionModal: React.FC<PaymentInstructionModalProps> = ({
  employeeId,
  employeeName,
  existingInstruction,
  onClose,
  onSave,
}) => {
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [holderName, setHolderName] = useState(employeeName);
  const [bankName, setBankName] = useState('');
  const [activeFrom, setActiveFrom] = useState(new Date().toISOString().split('T')[0]);
  const [ibanError, setIbanError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existingInstruction) {
      setHolderName(existingInstruction.holder_name);
      setBic(existingInstruction.bic || '');
      setBankName(existingInstruction.bank_name || '');
      if (existingInstruction.active_from) {
        setActiveFrom(existingInstruction.active_from.split('T')[0]);
      }
    }
  }, [existingInstruction]);

  const handleIbanChange = (value: string) => {
    const formatted = formatIBAN(value);
    setIban(formatted);

    if (value.trim() === '') {
      setIbanError(null);
    } else {
      const error = getIBANError(value);
      setIbanError(error);
    }
  };

  const handleSave = async () => {
    const error = getIBANError(iban);
    if (error) {
      setIbanError(error);
      return;
    }

    if (!holderName.trim()) {
      toast.error('Account holder is required');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        iban: cleanIBAN(iban),
        bic: bic.trim() || undefined,
        holder_name: holderName.trim(),
        bank_name: bankName.trim() || undefined,
        active_from: activeFrom,
      };

      if (existingInstruction) {
        await PaymentInstructionService.updatePaymentInstruction(employeeId, payload);
        toast.success('Payment instruction successfully updated');
      } else {
        await PaymentInstructionService.createPaymentInstruction(employeeId, payload);
        toast.success('Payment instruction successfully created');
      }

      setIban('');
      setBic('');
      setBankName('');
      onSave();
    } catch (error: any) {
      const traceId = error?.response?.headers?.['x-trace-id'] || error?.trace_id || 'N/A';
      const detail = error?.response?.data?.detail || error?.response?.data?.message || 'Save failed';

      if (error?.response?.status === 400 && detail.includes('iban')) {
        setIbanError('Invalid IBAN');
      }

      toast.error(`${detail}${traceId !== 'N/A' ? ` (Trace: ${traceId})` : ''}`);
    } finally {
      setSaving(false);
    }
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle: React.CSSProperties = {
    background: colors.bgCard,
    borderRadius: 12,
    padding: 32,
    maxWidth: 600,
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 20,
    fontWeight: 600,
    color: colors.navy,
    margin: 0,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    fontSize: 20,
    color: colors.textMuted,
    cursor: 'pointer',
  };

  const errorInputStyle: React.CSSProperties = {
    ...ps.inputStyle,
    borderColor: colors.danger,
  };

  const errorTextStyle: React.CSSProperties = {
    fontSize: 12,
    color: colors.danger,
    marginTop: 4,
  };

  const hintStyle: React.CSSProperties = {
    fontSize: 12,
    color: colors.textMuted,
    marginTop: 4,
  };

  const actionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
    marginTop: 24,
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>
            {existingInstruction ? 'Edit Payment Instruction' : 'Add Payment Instruction'}
          </h3>
          <button type="button" style={closeButtonStyle} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={ps.labelStyle}>
              IBAN <span style={{ color: colors.danger }}>*</span>
            </label>
            <div style={ibanError ? { ...ps.fieldWrapper, borderColor: colors.danger } : ps.fieldWrapper}>
              <input
                type="text"
                style={ibanError ? errorInputStyle : ps.inputStyle}
                value={iban}
                onChange={(e) => handleIbanChange(e.target.value)}
                placeholder="DE89 3704 0044 0532 0130 00"
                maxLength={34}
                autoComplete="off"
              />
            </div>
            {ibanError && <div style={errorTextStyle}>{ibanError}</div>}
            <div style={hintStyle}>
              IBAN (visible only during input; will be masked afterwards)
            </div>
          </div>

          <div>
            <label style={ps.labelStyle}>BIC</label>
            <div style={ps.fieldWrapper}>
              <input
                type="text"
                style={ps.inputStyle}
                value={bic}
                onChange={(e) => setBic(e.target.value.toUpperCase())}
                placeholder="COBADEFFXXX"
                maxLength={11}
              />
            </div>
          </div>

          <div>
            <label style={ps.labelStyle}>
              Account Holder <span style={{ color: colors.danger }}>*</span>
            </label>
            <div style={ps.fieldWrapper}>
              <input
                type="text"
                style={ps.inputStyle}
                value={holderName}
                onChange={(e) => setHolderName(e.target.value)}
                placeholder={employeeName}
              />
            </div>
          </div>

          <div>
            <label style={ps.labelStyle}>Bank Name</label>
            <div style={ps.fieldWrapper}>
              <input
                type="text"
                style={ps.inputStyle}
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="Commerzbank"
              />
            </div>
          </div>

          <div>
            <label style={ps.labelStyle}>
              Valid from <span style={{ color: colors.danger }}>*</span>
            </label>
            <div style={ps.fieldWrapper}>
              <input
                type="date"
                style={ps.inputStyle}
                value={activeFrom}
                onChange={(e) => setActiveFrom(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div style={actionsStyle}>
          <button type="button" style={ps.buttonSecondary} onClick={onClose} disabled={saving}>
            <FaTimes /> Cancel
          </button>
          <button
            type="button"
            style={ps.buttonPrimary}
            onClick={handleSave}
            disabled={saving || !!ibanError || !iban.trim()}
          >
            <FaSave /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentInstructionModal;
